import React, { useCallback, useEffect, useState } from "react";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  makeStyles,
  TextField,
  Typography,
} from "@material-ui/core";
import DeleteOutline from "@material-ui/icons/DeleteOutline";
import Edit from "@material-ui/icons/Edit";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import TagChip from "../TagChip";

const PALETTE = [
  "#B80000", "#DB3E00", "#FCCB00", "#008B02", "#006B76", "#1273DE",
  "#5300EB", "#AB149E", "#4D4D4D", "#999999", "#FE9200", "#16A5A5",
];

const useStyles = makeStyles((theme) => ({
  form: { margin: theme.spacing(1, 0, 2) },
  swatches: { display: "flex", flexWrap: "wrap", gap: theme.spacing(0.75), margin: theme.spacing(1, 0) },
  swatch: {
    width: 26,
    height: 26,
    borderRadius: "50%",
    cursor: "pointer",
    border: "2px solid transparent",
  },
  swatchActive: { borderColor: theme.palette.text.primary },
  count: { marginLeft: theme.spacing(1), color: theme.palette.text.secondary },
}));

const emptyForm = { id: null, name: "", color: PALETTE[5] };

const TagsModal = ({ open, onClose, onChanged }) => {
  const classes = useStyles();
  const [tags, setTags] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/tags");
      setTags(data.tags);
    } catch (err) {
      toastError(err);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setForm(emptyForm);
      load();
    }
  }, [open, load]);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (form.id) {
        await api.put(`/tags/${form.id}`, { name: form.name, color: form.color });
      } else {
        await api.post("/tags", { name: form.name, color: form.color });
      }
      setForm(emptyForm);
      await load();
      onChanged();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (tag) => {
    if (!window.confirm(i18n.t("tags.confirmDelete", { name: tag.name }))) return;
    try {
      await api.delete(`/tags/${tag.id}`);
      toast.success(i18n.t("tags.toasts.deleted"));
      if (form.id === tag.id) setForm(emptyForm);
      await load();
      onChanged();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth scroll="paper">
      <DialogTitle>{i18n.t("tags.title")}</DialogTitle>
      <DialogContent dividers>
        <div className={classes.form}>
          <TextField
            label={form.id ? i18n.t("tags.editName") : i18n.t("tags.newName")}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === "Enter" && form.name.trim()) handleSave();
            }}
            variant="outlined"
            margin="dense"
            fullWidth
            inputProps={{ maxLength: 40 }}
          />
          <div className={classes.swatches}>
            {PALETTE.map((color) => (
              <div
                key={color}
                role="button"
                aria-label={color}
                className={`${classes.swatch} ${form.color === color ? classes.swatchActive : ""}`}
                style={{ backgroundColor: color }}
                onClick={() => setForm({ ...form, color })}
              />
            ))}
          </div>
          <Button
            variant="contained"
            color="primary"
            size="small"
            disabled={!form.name.trim() || saving}
            onClick={handleSave}
          >
            {form.id ? i18n.t("tags.buttons.save") : i18n.t("tags.buttons.add")}
          </Button>
          {form.id && (
            <Button size="small" style={{ marginLeft: 8 }} onClick={() => setForm(emptyForm)}>
              {i18n.t("tags.buttons.cancelEdit")}
            </Button>
          )}
        </div>

        {tags.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            {i18n.t("tags.empty")}
          </Typography>
        ) : (
          <List dense disablePadding>
            {tags.map((tag) => (
              <ListItem key={tag.id} disableGutters>
                <ListItemText
                  disableTypography
                  primary={
                    <>
                      <TagChip tag={tag} />
                      <span className={classes.count}>
                        {i18n.t("tags.contactsCount", { count: tag.contacts })}
                      </span>
                    </>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton
                    size="small"
                    onClick={() => setForm({ id: tag.id, name: tag.name, color: tag.color })}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(tag)}>
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{i18n.t("tags.buttons.close")}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TagsModal;
